import React, {useRef, useState} from "react";
import {Box, Button, FormControlLabel, IconButton, makeStyles, Popover, SvgIcon, Switch, TextField, Tooltip, Typography} from "@material-ui/core";
import {Settings as SettingsIcon} from "react-feather";
import useSettings from "src/hooks/useSettings";
import {HOSTS_CONFIG, THEMES} from "src/constants";
import {updateLanguagePreference} from "src/api/profile";
import _ from "lodash";

import {useTranslation} from "react-i18next";

const useStyles = makeStyles(theme => ({
    badge: {
        height: 10,
        width: 10,
        borderRadius: 5,
        marginTop: 10,
        marginRight: 5
    },
    popover: {
        width: 320,
        padding: theme.spacing(2)
    }
}));

const Settings = () => {
    const classes = useStyles();
    const ref = useRef(null);
    const {settings, saveSettings} = useSettings();
    const [isOpen, setOpen] = useState(false);
    const [values, setValues] = useState({
        direction: settings.direction,
        responsiveFontSizes: settings.responsiveFontSizes,
        theme: settings.theme,
        language: settings.language
    });
    const hostname = window.location.hostname;

    const {t, i18n} = useTranslation("settings");

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (field, value) => {
        setValues({
            ...values,
            [field]: value
        });
    };

    const handleSave = () => {
        setOpen(false);
        saveSettings(values);
        if (values.language) {
            updateLanguagePreference(values.language).catch(console.error);
        }
    };

    return (
        <>
            <Tooltip title={t("title")}>
                <IconButton color="inherit" onClick={handleOpen} ref={ref}>
                    <SvgIcon fontSize="small">
                        <SettingsIcon/>
                    </SvgIcon>
                </IconButton>
            </Tooltip>
            <Popover
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center"
                }}
                classes={{paper: classes.popover}}
                anchorEl={ref.current}
                onClose={handleClose}
                open={isOpen}
            >
                <Typography variant="h4" color="textPrimary">
                    {t("settings")}
                </Typography>
                <Box mt={2} px={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.direction === "rtl"}
                                edge="start"
                                name="direction"
                                onChange={event => handleChange("direction", event.target.checked ? "rtl" : "ltr")}
                            />
                        }
                        label={t("rtl")}
                    />
                </Box>
                <Box mt={2} px={1}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={values.responsiveFontSizes}
                                edge="start"
                                name="direction"
                                onChange={event => handleChange("responsiveFontSizes", event.target.checked)}
                            />
                        }
                        label={t("responsiveFontSize")}
                    />
                </Box>
                <Box mt={2}>
                    <TextField
                        fullWidth
                        label={t("theme")}
                        name="theme"
                        onChange={event => handleChange("theme", event.target.value)}
                        select
                        SelectProps={{native: true}}
                        value={values.theme}
                        variant="outlined"
                    >
                        {Object.keys(THEMES).map(theme => (
                            <option key={theme} value={theme}>
                                {t(`themeSelect.${theme}`)}
                            </option>
                        ))}
                    </TextField>
                </Box>
                <Box mt={2}>
                    <TextField
                        fullWidth
                        label={t("language")}
                        name="language"
                        onChange={event => handleChange("language", event.target.value)}
                        select
                        SelectProps={{native: true}}
                        value={values.language || i18n.language}
                        variant="outlined"
                    >
                        {_.get(HOSTS_CONFIG, `${hostname}`).supportedLanguages.map(language => (
                            <option key={language} value={language}>
                                {t(`languageSelect.${language}`)}
                            </option>
                        ))}
                    </TextField>
                </Box>
                <Box mt={2}>
                    <Button variant="contained" color="secondary" fullWidth onClick={handleSave}>
                        {t("saveSettings")}
                    </Button>
                </Box>
            </Popover>
        </>
    );
};

export default Settings;
